/* Dual Cart Drawer Rendering and Management
 * Renders cart items from localStorage in separate US/CN drawers
 * Virtual carts are synced to Shopify's actual cart only at checkout
 */

(function() {
  'use strict';
  
  const STORAGE_KEYS = { us: 'cart_us', cn: 'cart_cn' };
  const api = window.WarehouseCart || (window.WarehouseCart = {});

  // Read cart from localStorage
  const readCart = (warehouse) => {
    try {
      const key = STORAGE_KEYS[warehouse] || STORAGE_KEYS.us;
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch {
      return [];
    }
  };

  // Write cart to localStorage
  const writeCart = (warehouse, items) => {
    const key = STORAGE_KEYS[warehouse] || STORAGE_KEYS.us;
    localStorage.setItem(key, JSON.stringify(items));
  };

  // Format money with thousands separators
  const formatMoney = (cents) => {
    const dollars = (cents || 0) / 100;
    return `$${dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  // Render cart items for a specific warehouse
  const renderCartItems = async (warehouse) => {
    console.log('Rendering cart for warehouse:', warehouse);
    const cart = readCart(warehouse);
    const drawerIdPrefix = warehouse.toUpperCase();
    const itemsContainer = document.getElementById(`CartDrawer${drawerIdPrefix}-Items`);
    const totalContainer = document.getElementById(`CartDrawer${drawerIdPrefix}-Total`);
    const drawer = document.getElementById(`CartDrawer-${drawerIdPrefix}`);

    if (!itemsContainer || !drawer) {
      console.error(`Cart drawer container not found for ${warehouse}`);
      return;
    }

    // Show/hide empty state
    if (cart.length === 0) {
      drawer.classList.add('is-empty');
      itemsContainer.innerHTML = '';
      if (totalContainer) totalContainer.textContent = '$0.00';
      return;
    }

    drawer.classList.remove('is-empty');

    let totalPrice = 0;
    const rowsHtml = [];

    for (const item of cart) {
      try {
        console.log('=== Processing Cart Item ===');
        console.log('Raw item data:', JSON.parse(JSON.stringify(item)));
        
        // Enrich item data if missing
        if (!item.price || !item.image || !item.product_title) {
          console.log('Item missing data, fetching from API:', item.id);
          const enrichedData = await window.WarehouseCart?.fetchProductInfo?.(item.id);
          if (enrichedData) {
            console.log('Enriched data received:', enrichedData);
            Object.assign(item, enrichedData);
            // Update the cart in storage with enriched data
            const updatedCart = readCart(warehouse);
            const itemIndex = updatedCart.findIndex(i => String(i.id) === String(item.id));
            if (itemIndex >= 0) {
              updatedCart[itemIndex] = item;
              writeCart(warehouse, updatedCart);
            }
          }
        }

        const quantity = item.quantity || 1;
        const price = item.price || 0;
        const lineTotal = price * quantity;
        totalPrice += lineTotal;

        let productTitle = item.product_title || 'Product';
        let variantTitle = item.variant_title || '';
        
        // Debug: log the titles to see what we're getting
        console.log('Product Title:', productTitle);
        console.log('Original Variant Title:', variantTitle);
        
        // Check if product_title itself contains duplicate (separated by newline or repeated)
        const lines = productTitle.split(/[\n\r]+/).map(l => l.trim()).filter(l => l);
        if (lines.length > 1 && lines[0] === lines[1]) {
          console.log('Product title contains duplicate lines - fixing');
          productTitle = lines[0];
        }
        
        // Clean up variant title to prevent duplicates
        if (variantTitle) {
          // If variant title is exactly the same as product title, hide it
          if (variantTitle.trim().toLowerCase() === productTitle.trim().toLowerCase()) {
            console.log('Variant title matches product title exactly - hiding variant');
            variantTitle = '';
          }
          // If variant title contains the full product title, it's a duplicate
          else if (variantTitle.toLowerCase().includes(productTitle.toLowerCase())) {
            console.log('Variant title contains product title - hiding variant');
            variantTitle = '';
          }
          // If it's "Default Title", hide it
          else if (variantTitle === 'Default Title' || variantTitle.toLowerCase() === 'default') {
            console.log('Default variant title - hiding');
            variantTitle = '';
          }
        }
        
        console.log('Final Product Title:', productTitle);
        console.log('Final Variant Title:', variantTitle);
        
        const productUrl = item.url || '#';
        const imageUrl = item.image || '';
        const displayImage = imageUrl ? imageUrl.replace('.jpg', '_300x.jpg').replace('.png', '_300x.png') : '';

        // New layout: quantity controls at bottom of the details column
        rowsHtml.push(`
          <tr class="cart-item" data-variant-id="${item.id}" role="row">
            <td class="cart-item__media" role="cell">
              ${displayImage ? `<img class="cart-item__image" src="${displayImage}" alt="${productTitle}" loading="lazy">` : ''}
            </td>
            <td class="cart-item__details" role="cell" colspan="2">
              <div class="cart-item__stack">
                <div class="cart-item__top-row">
                  <div class="cart-item__info">
                    <a href="${productUrl}" class="cart-item__name h4 break">${productTitle}</a>
                    ${variantTitle && variantTitle !== 'Default Title' ? `<div class="product-option">${variantTitle}</div>` : ''}
                    ${item.properties && item.properties.Warehouse ? `<div class="product-option warehouse-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 4px;">
                        <path d="M8 0L0 4v4c0 4.4 3.6 8 8 8s8-3.6 8-8V4L8 0zm0 2l6 2.5V8c0 3.3-2.7 6-6 6s-6-2.7-6-6V4.5L8 2z"/>
                      </svg>
                      Ships from: ${item.properties.Warehouse}
                    </div>` : ''}
                  </div>
                  <div class="cart-item__price">${formatMoney(lineTotal)}</div>
                </div>

                <div class="cart-item__details-bottom">
                  <div class="quantity-controls">
                    <button class="quantity__button" type="button" data-action="decrease" data-variant-id="${item.id}" data-warehouse="${warehouse}">−</button>
                    <input class="quantity__input" type="number" value="${quantity}" min="1" data-variant-id="${item.id}" data-warehouse="${warehouse}">
                    <button class="quantity__button" type="button" data-action="increase" data-variant-id="${item.id}" data-warehouse="${warehouse}">+</button>
                  </div>
                  <button type="button" class="cart-remove-button" data-variant-id="${item.id}" data-warehouse="${warehouse}" aria-label="Remove item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.86.19 1.09.48c.16.24.27.62.31 1.02h-2.8c.04-.4.15-.78.31-1.02z" fill="currentColor"></path></svg>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        `);
      } catch (error) {
        console.error('Error loading product:', error);
      }
    }

    itemsContainer.innerHTML = rowsHtml.join('');
    if (totalContainer) {
      totalContainer.textContent = formatMoney(totalPrice);
    }

    // Bind event listeners
    bindCartEvents(warehouse);
  };

  // Bind event listeners to cart items
  const bindCartEvents = (warehouse) => {
    const drawerIdPrefix = warehouse.toUpperCase();
    const drawer = document.getElementById(`CartDrawer-${drawerIdPrefix}`);
    if (!drawer) return;

    // Quantity buttons
    drawer.querySelectorAll('.quantity__button[data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        const variantId = btn.dataset.variantId;
        const wh = btn.dataset.warehouse;
        updateQuantity(wh, variantId, action);
      });
    });

    // Quantity inputs
    drawer.querySelectorAll('.quantity__input').forEach(input => {
      input.addEventListener('change', () => {
        const variantId = input.dataset.variantId;
        const wh = input.dataset.warehouse;
        const newQty = parseInt(input.value) || 0;
        setQuantity(wh, variantId, newQty);
      });
    });

    // Remove buttons
    drawer.querySelectorAll('.cart-remove-button').forEach(btn => {
      btn.addEventListener('click', () => {
        const variantId = btn.dataset.variantId;
        const wh = btn.dataset.warehouse;
        removeItem(wh, variantId);
      });
    });
  };

  // Update quantity
  const updateQuantity = (warehouse, variantId, action) => {
    const cart = readCart(warehouse);
    const item = cart.find(i => String(i.id) === String(variantId));
    if (item) {
      if (action === 'increase') {
        item.quantity = (item.quantity || 1) + 1;
      } else if (action === 'decrease') {
        item.quantity = Math.max(0, (item.quantity || 1) - 1);
      }
      
      if (item.quantity === 0) {
        removeItem(warehouse, variantId);
        return;
      }
      
      writeCart(warehouse, cart);
      renderCartItems(warehouse);
      if (api.renderCounts) api.renderCounts();
    }
  };

  // Set specific quantity
  const setQuantity = (warehouse, variantId, quantity) => {
    if (quantity <= 0) {
      removeItem(warehouse, variantId);
      return;
    }

    const cart = readCart(warehouse);
    const item = cart.find(i => String(i.id) === String(variantId));
    if (item) {
      item.quantity = quantity;
      writeCart(warehouse, cart);
      renderCartItems(warehouse);
      if (api.renderCounts) api.renderCounts();
    }
  };

  // Remove item
  const removeItem = (warehouse, variantId) => {
    const cart = readCart(warehouse);
    const filtered = cart.filter(i => String(i.id) !== String(variantId));
    writeCart(warehouse, filtered);
    renderCartItems(warehouse);
    if (api.renderCounts) api.renderCounts();
    if (api.notify) api.notify('Item removed from cart');
  };

  // Open drawer
  const openDrawer = (warehouse) => {
    console.log('openDrawer called for:', warehouse);
    const drawerIdPrefix = warehouse.toUpperCase();
    const drawer = document.getElementById(`CartDrawer-${drawerIdPrefix}`);
    
    if (!drawer) {
      console.error(`Drawer not found: CartDrawer-${drawerIdPrefix}`);
      return;
    }

    // Close other drawer
    const otherWarehouse = warehouse === 'us' ? 'cn' : 'us';
    const otherDrawer = document.getElementById(`CartDrawer-${otherWarehouse.toUpperCase()}`);
    if (otherDrawer && otherDrawer.classList.contains('active')) {
      closeDrawer(otherWarehouse);
    }

    // Render and open
    renderCartItems(warehouse);
    
    drawer.style.visibility = 'visible';
    requestAnimationFrame(() => {
      drawer.classList.add('animate', 'active');
    });

    document.body.classList.add('overflow-hidden');
  };

  // Close drawer
  const closeDrawer = (warehouse) => {
    const drawerIdPrefix = warehouse.toUpperCase();
    const drawer = document.getElementById(`CartDrawer-${drawerIdPrefix}`);
    if (!drawer) return;

    drawer.classList.remove('active', 'animate');
    setTimeout(() => {
      if (!drawer.classList.contains('active')) {
        drawer.style.visibility = 'hidden';
      }
    }, 300);
    document.body.classList.remove('overflow-hidden');
  };

  // Bind header cart links
  const bindHeaderLinks = () => {
    const headerLinks = document.querySelectorAll('.dual-cart-link[data-warehouse]');
    console.log('[Dual Cart] Found header links:', headerLinks.length);
    
    headerLinks.forEach((link, index) => {
      console.log(`[Dual Cart] Binding link ${index}:`, link.dataset.warehouse);
      
      // Use direct event listener (not delegation)
      link.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        const warehouse = this.dataset.warehouse;
        console.log('[Dual Cart] Link clicked! Opening:', warehouse);
        openDrawer(warehouse);
        return false;
      };
    });
  };

  // Bind close buttons and overlays
  const bindCloseHandlers = () => {
    // Close buttons
    document.querySelectorAll('[id^="CartDrawer-"] .drawer__close').forEach(btn => {
      btn.onclick = function(e) {
        e.preventDefault();
        const drawer = this.closest('[id^="CartDrawer-"]');
        if (drawer) {
          const warehouse = drawer.id.includes('US') ? 'us' : 'cn';
          console.log('[Dual Cart] Closing drawer:', warehouse);
          closeDrawer(warehouse);
        }
        return false;
      };
    });

    // Overlay/Backdrop clicks
    document.querySelectorAll('[id^="CartDrawer-"]').forEach(drawer => {
      drawer.onclick = function(e) {
        // If the click is NOT inside the inner drawer content, it's a click on the backdrop
        if (!e.target.closest('.drawer__inner')) {
          const warehouse = this.id.includes('US') ? 'us' : 'cn';
          console.log('[Dual Cart] Closing drawer (backdrop click):', warehouse);
          closeDrawer(warehouse);
        }
      };
    });
  };

  // Bind checkout buttons
  const bindCheckoutButtons = () => {
    document.querySelectorAll('[data-warehouse-checkout]').forEach(btn => {
      btn.onclick = function() {
        const warehouse = this.dataset.warehouseCheckout;
        console.log('[Dual Cart] Checkout clicked for:', warehouse);
        if (api.syncAndCheckout) {
          api.syncAndCheckout(warehouse);
        }
      };
    });
  };

  // Bind “View cart” links to sync and navigate
  const bindViewCartLinks = () => {
    const links = document.querySelectorAll('.drawer__view-cart-link');
    links.forEach(link => {
      link.onclick = async function(e) {
        e.preventDefault();
        e.stopPropagation();

        const drawer = this.closest('[id^="CartDrawer-"]');
        const warehouse =
          drawer?.dataset.warehouse ||
          (drawer?.id.includes('US') ? 'us' : 'cn');

        if (api.syncAndGoToCart) {
          await api.syncAndGoToCart(warehouse);
        } else {
          // Fallback: still navigate if API isn’t available
          window.location.href = this.href;
        }
      };
    });
  };

  // Initialize
  const init = () => {
    console.log('[Dual Cart] Initializing cart-drawer-dual.js...');
    
    // Check for required elements
    const usDrawer = document.getElementById('CartDrawer-US');
    const cnDrawer = document.getElementById('CartDrawer-CN');
    console.log('[Dual Cart] US Drawer:', usDrawer ? '✓' : '✗');
    console.log('[Dual Cart] CN Drawer:', cnDrawer ? '✓' : '✗');
    
    bindHeaderLinks();
    bindCloseHandlers();
    bindCheckoutButtons();
    bindViewCartLinks();
    
    // Update counts
    if (api.renderCounts) {
      api.renderCounts();
    }
    
    console.log('[Dual Cart] Initialization complete ✓');
  };

  // Export methods to API
  api.renderCartItems = renderCartItems;
  api.openDrawer = openDrawer;
  api.closeDrawer = closeDrawer;
  api.initDualCart = init; // Export init for manual re-initialization

  // Initialize - try multiple times to ensure it works
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(init, 100);
    });
  } else {
    setTimeout(init, 100);
  }
  
  // Also bind on window load as a backup
  window.addEventListener('load', () => {
    setTimeout(init, 200);
  });
})();
